import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "wasp/client/auth";
import {
  getAllLocations,
  deleteLocation,
  useQuery,
  getLocationById,
  updateLocation,
  getLocationTypesForDropdown,
} from "wasp/client/operations";
import { type Location } from "wasp/entities";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  MoreHorizontal,
  Plus,
  Search,
  MapPin,
  Users,
  FileText,
  Calendar,
  Building2,
  Network,
  Filter,
} from "lucide-react";
import DefaultLayout from "../../admin/layout/DefaultLayout";
import LoadingSpinner from "../../admin/layout/LoadingSpinner";
import { EnhancedActionButton, useResponsiveLayout } from "../../components/ui/enhanced-page-layout";
import { TableFilterLayout } from "../../components/ui/table-filter-layout";
import { AdminFlyoutPane } from "../../admin/components/AdminFlyoutPane";
import {
  FlyoutGridLayout,
  FlyoutGridSection,
  FlyoutGridRow,
} from "../../admin/components/FlyoutGridLayout";
import { Card, CardContent, CardHeader } from "../../components/ui/card";
import { toast } from "../../hooks/use-toast";

const AdminLocationsPage = () => {
  const { data: user } = useAuth();

  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [isPublicFilter, setIsPublicFilter] = useState<string>("all");
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(
    null,
  );
  const [flyoutMode, setFlyoutMode] = useState<"view" | "edit" | "delete">("view");
  const { isFilterExpanded, setIsFilterExpanded } = useResponsiveLayout();
  const [deletePolicy, setDeletePolicy] = useState<"DELETE_ALL" | "REASSIGN">("DELETE_ALL");
  const [reassignLocationId, setReassignLocationId] = useState<string>("");

  // Editable fields
  const [editFormData, setEditFormData] = useState({
    name: "",
    typeId: "", // Changed from type to typeId
    isPublic: false,
    allowPublicSubscription: true,
    defaultVisibility: "VERIFIED",
    publicDescription: "",
    address: "",
    city: "",
    state: "",
    country: "",
    postalCode: "",
    timezone: "UTC",
    latitude: "",
    longitude: "",
    // Hub fields
    isHub: false,
    aggregateChildContent: true,
    parentLocationId: "" as string | null,
  });

  const { data, isLoading, error, refetch } = useQuery(getAllLocations, {
    limit: 0,
    search: search || undefined,
    type: typeFilter === "all" ? undefined : typeFilter,
    isPublic: isPublicFilter === "all" ? undefined : isPublicFilter === "true",
  });

  // Fetch selected location details for flyout
  const { data: selectedLocation, isLoading: loadingLocation } = useQuery(
    getLocationById,
    { id: selectedLocationId || "" },
    { enabled: !!selectedLocationId, keepPreviousData: true },
  );

  // Fetch location types for dropdown
  const { data: locationTypes } = useQuery(getLocationTypesForDropdown);
  const locationTypesArray = (locationTypes as any) || [];

  const locationsData = data as
    | { locations: any[]; total: number; page: number; totalPages: number }
    | undefined;
  const hasLocations = !!locationsData?.locations?.length;
  // Header (approx 6rem with new padding) + TableFilterLayout Columns (approx 2.5rem)
  // Collapsed: ~8.5rem + 1rem breathing room = 9.5rem
  // Expanded: ~8.5rem + Filters (~4rem) - adjusted to remove gap
  // Header (approx 6rem with new padding) + TableFilterLayout Columns (approx 2.5rem)
  // Collapsed: ~8.5rem + 1rem breathing room = 9.5rem
  // Expanded: ~8.5rem + Filters (~4rem) - adjusted to remove gap
  const contentPaddingTop = isFilterExpanded
    ? "pt-[1rem] md:pt-[12.5rem]"
    : "pt-[1rem] md:pt-[9.5rem]";

  // Initialize edit fields when location is selected
  useEffect(() => {
    if (selectedLocation) {
      const loc = selectedLocation as any; // Extended type with hub fields
      setEditFormData({
        name: loc.name || "",
        typeId: loc.typeId || "", // Changed from type to typeId
        isPublic: loc.isPublic || false,
        allowPublicSubscription: loc.allowPublicSubscription ?? true,
        defaultVisibility: loc.defaultVisibility || "VERIFIED",
        publicDescription: loc.publicDescription || "",
        address: loc.address || "",
        city: loc.city || "",
        state: loc.state || "",
        country: loc.country || "",
        postalCode: loc.postalCode || "",
        timezone: loc.timezone || "UTC",
        latitude: loc.latitude?.toString() || "",
        longitude: loc.longitude?.toString() || "",
        // Hub fields
        isHub: loc.isHub || false,
        aggregateChildContent: loc.aggregateChildContent ?? true,
        parentLocationId: loc.parentLocationId || null,
      });
    }
  }, [selectedLocation]);

  // Check if any changes have been made
  const locationData = selectedLocation as any; // Extended type with hub fields
  const hasChanges = locationData
    ? editFormData.name !== (locationData.name || "") ||
    editFormData.typeId !== (locationData.typeId || "") || // Changed from type to typeId
    editFormData.isPublic !== (locationData.isPublic || false) ||
    editFormData.allowPublicSubscription !==
    (locationData.allowPublicSubscription ?? true) ||
    editFormData.defaultVisibility !==
    (locationData.defaultVisibility || "VERIFIED") ||
    editFormData.publicDescription !==
    (locationData.publicDescription || "") ||
    editFormData.address !== (locationData.address || "") ||
    editFormData.city !== (locationData.city || "") ||
    editFormData.state !== (locationData.state || "") ||
    editFormData.country !== (locationData.country || "") ||
    editFormData.postalCode !== (locationData.postalCode || "") ||
    editFormData.timezone !== (locationData.timezone || "UTC") ||
    editFormData.latitude !== (locationData.latitude?.toString() || "") ||
    editFormData.longitude !== (locationData.longitude?.toString() || "") ||
    // Hub fields
    editFormData.isHub !== (locationData.isHub || false) ||
    editFormData.aggregateChildContent !==
    (locationData.aggregateChildContent ?? true) ||
    editFormData.parentLocationId !== (locationData.parentLocationId || null)
    : false;

  // Get list of potential parent locations (hubs that this location can be added to)
  const availableParentLocations =
    locationsData?.locations?.filter(
      (loc: any) =>
        loc.id !== selectedLocationId && // Cannot be its own parent
        loc.isHub && // Must be a hub
        loc.parentLocationId !== selectedLocationId, // Cannot be a child of this location
    ) || [];

  const handleLocationClick = (locationId: string) => {
    setSelectedLocationId(locationId);
    setFlyoutMode("view");
  };

  const handleEdit = () => {
    setFlyoutMode("edit");
  };

  const handleSave = async () => {
    if (!locationData) return;

    try {
      await updateLocation({
        id: locationData.id,
        ...editFormData,
        latitude: editFormData.latitude
          ? parseFloat(editFormData.latitude)
          : undefined,
        longitude: editFormData.longitude
          ? parseFloat(editFormData.longitude)
          : undefined,
      });

      await refetch();
      setFlyoutMode("view");

      toast({
        title: "Success",
        description: "Location updated successfully",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to update location",
        variant: "destructive",
      });
    }
  };

  const handleConfirmDelete = async () => {
    if (!locationData) return;
    
    // Check if location has contents to require policy
    const hasItems =
      ((locationData as any)._count?.memberships > 0) ||
      ((locationData as any)._count?.content > 0) ||
      ((locationData as any)._count?.events > 0) ||
      ((locationData as any)._count?.childLocations > 0);

    if (hasItems && deletePolicy === "REASSIGN" && !reassignLocationId) {
      toast({
        title: "Error",
        description: "Please select a location to reassign items to.",
        variant: "destructive",
      });
      return;
    }

    const idToDelete = locationData.id;
    try {
      setSelectedLocationId(null);
      await deleteLocation({ 
        id: idToDelete,
        deletePolicy: hasItems ? deletePolicy : undefined,
        reassignLocationId: hasItems && deletePolicy === "REASSIGN" ? reassignLocationId : undefined,
      });
      await refetch();
      toast({
        title: "Success",
        description: "Location deleted successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete location: " + (error as Error).message,
        variant: "destructive",
      });
      // Restore selection if delete failed
      setSelectedLocationId(idToDelete);
      setFlyoutMode("delete");
    }
  };

  const handleDelete = () => {
    setDeletePolicy("DELETE_ALL");
    setReassignLocationId("");
    setFlyoutMode("delete");
  };

  // Navigation helpers
  const currentLocationIndex =
    locationsData?.locations?.findIndex((l) => l.id === selectedLocationId) ??
    -1;
  const hasPrevious = currentLocationIndex > 0;
  const hasNext =
    currentLocationIndex >= 0 &&
    currentLocationIndex < (locationsData?.locations?.length ?? 0) - 1;

  const handleNavigatePrevious = () => {
    if (hasPrevious && locationsData?.locations) {
      const previousLocation =
        locationsData.locations[currentLocationIndex - 1];
      setSelectedLocationId(previousLocation.id);
    }
  };

  const handleNavigateNext = () => {
    if (hasNext && locationsData?.locations) {
      const nextLocation = locationsData.locations[currentLocationIndex + 1];
      setSelectedLocationId(nextLocation.id);
    }
  };

  const getLocationTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      APARTMENT_BUILDING: "text-blue-600",
      INNOVATION_HUB: "text-green-600",
      COWORKING_SPACE: "text-purple-600",
      SHOPPING_CENTER: "text-orange-600",
      CAMPUS: "text-indigo-600",
      COMMUNITY_CENTER: "text-pink-600",
      MIXED_USE: "text-gray-600",
      OTHER: "text-slate-600",
    };
    return colors[type] || colors.OTHER;
  };

  if (!user?.isAdmin) {
    return <div>Access denied. Admin privileges required.</div>;
  }

  return (
    <DefaultLayout user={user}>
      <div className="w-full">
        {/* Fixed Header - hidden on small screens; min-h keeps table headers below title */}
        <div className="hidden md:block fixed top-[var(--header-height)] left-[var(--sidebar-width)] right-0 z-30 bg-white dark:bg-sidebar-active transition-colors border-b border-border min-h-[6rem] flex flex-col justify-center">
          <div className="px-6 pt-6 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Location Management
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                  Manage all locations in the system (
                  {locationsData?.total || 0} total)
                </p>
              </div>
              <div className="flex items-center gap-3">
                <EnhancedActionButton
                  onClick={() => setIsFilterExpanded(!isFilterExpanded)}
                  icon={Filter}
                  label=""
                  className={isFilterExpanded ? "opacity-80" : ""}
                />
                <Link to="/admin/locations/new">
                  <EnhancedActionButton
                    onClick={() => { }}
                    icon={Plus}
                    label={<span className="hidden sm:inline">Create Location</span>}
                  />
                </Link>
              </div>
            </div>
          </div>
        </div>

        <TableFilterLayout
          className="hidden md:block fixed top-[calc(var(--header-height)+6rem)] left-[var(--sidebar-width)] right-0 z-20"
          isExpanded={isFilterExpanded}
          columns={[
            { title: "Location", span: 4 },
            { title: "Type", span: 2 },
            { title: "Visibility", span: 1 },
            { title: "Location Code", span: 1 },
            { title: "Address", span: 2 },
            { title: "Stats", span: 2 },
          ]}
          filters={[
            {
              span: 4,
              content: (
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 transform text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mr-4"
                  />
                </div>
              ),
            },
            {
              span: 2,
              content: (
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="appearance-none w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mr-4"
                >
                  <option value="all">All types</option>
                  <option value="APARTMENT_BUILDING">Apartment Building</option>
                  <option value="INNOVATION_HUB">Innovation Hub</option>
                  <option value="COWORKING_SPACE">Coworking Space</option>
                  <option value="SHOPPING_CENTER">Shopping Center</option>
                  <option value="CAMPUS">Campus</option>
                  <option value="COMMUNITY_CENTER">Community Center</option>
                  <option value="MIXED_USE">Mixed Use</option>
                  <option value="OTHER">Other</option>
                </select>
              ),
            },
            {
              span: 1,
              content: (
                <select
                  value={isPublicFilter}
                  onChange={(e) => setIsPublicFilter(e.target.value)}
                  className="appearance-none w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mr-4"
                >
                  <option value="all">All</option>
                  <option value="true">Public</option>
                  <option value="false">Private</option>
                </select>
              ),
            },
            // Empty spacers to align with remaining columns (Location Code: 1, Address: 2, Stats: 2)
            { span: 1, content: <div className="h-10" /> },
            { span: 2, content: <div className="h-10" /> },
            { span: 2, content: <div className="h-10" /> },
          ]}
        />

        {/* Scrollable Content */}
        <div
          className={`flex-1 overflow-y-auto transition-[padding] duration-300 ease-in-out ${contentPaddingTop}`}
        >
          <div className="w-full">
            {isLoading && <LoadingSpinner />}
            {error && (
              <div className="text-red-600">
                Error loading locations: {error.message}
              </div>
            )}

            {locationsData?.locations && locationsData.locations.length > 0 ? (
              <div className="w-full">
                {/* Card view - small screens only */}
                <div className="md:hidden px-4 space-y-4">
                  {locationsData.locations.map(
                    (location: Location & { _count: any }) => (
                      <Card
                        key={location.id}
                        className="cursor-pointer hover:shadow-md transition-shadow"
                        onClick={() => handleLocationClick(location.id)}
                      >
                        <CardHeader className="pb-2">
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 min-w-0">
                              {(location as any).isHub && (
                                <Building2 className="w-4 h-4 text-primary flex-shrink-0" />
                              )}
                              <span className="font-medium text-gray-900 dark:text-white truncate">
                                {location.name}
                              </span>
                            </div>
                            <span
                              className={`text-sm font-medium flex-shrink-0 ${location.isPublic
                                ? "text-green-600"
                                : "text-gray-600"
                                }`}
                            >
                              {location.isPublic ? "Public" : "Private"}
                            </span>
                          </div>
                          {(location as any).isHub &&
                            (location as any)._count?.childLocations > 0 && (
                              <div className="text-xs text-primary mt-0.5">
                                Hub with{" "}
                                {(location as any)._count.childLocations}{" "}
                                location(s)
                              </div>
                            )}
                          {(location as any).parentLocation && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                              Child of {(location as any).parentLocation.name}
                            </div>
                          )}
                          {location.publicDescription &&
                            !(location as any).isHub &&
                            !(location as any).parentLocation && (
                              <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 mt-1">
                                {location.publicDescription}
                              </p>
                            )}
                        </CardHeader>
                        <CardContent className="pt-0 space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-500 dark:text-gray-400">
                              Type
                            </span>
                            <span
                              className="font-medium"
                              style={{
                                color:
                                  (location as any).locationType?.color ||
                                  "#64748B",
                              }}
                            >
                              {(location as any).locationType?.name ||
                                "Unknown"}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500 dark:text-gray-400">
                              Code
                            </span>
                            <span className="text-gray-700 dark:text-gray-300 font-mono">
                              {location.locationCode}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500 dark:text-gray-400">
                              Address
                            </span>
                            <span className="text-gray-700 dark:text-gray-300">
                              {location.city}, {location.state},{" "}
                              {location.country}
                            </span>
                          </div>
                          <div className="flex gap-4 pt-2 text-gray-600 dark:text-gray-400">
                            <span className="flex items-center gap-1">
                              <Users className="w-3 h-3" />{" "}
                              {location._count?.memberships || 0}
                            </span>
                            <span className="flex items-center gap-1">
                              <FileText className="w-3 h-3" />{" "}
                              {location._count?.content || 0}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />{" "}
                              {location._count?.events || 0}
                            </span>
                          </div>
                          <div
                            className="flex justify-end pt-2"
                            onClick={(e) => e?.stopPropagation()}
                          >
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  <MoreHorizontal className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem asChild>
                                  <Link
                                    to={`/admin/locations/${location.id}/edit`}
                                  >
                                    Edit location (section visibility &amp;
                                    more)
                                  </Link>
                                </DropdownMenuItem>
                                <DropdownMenuItem asChild>
                                  <Link
                                    to={`/locations/${location.locationCode}`}
                                  >
                                    View Public Page
                                  </Link>
                                </DropdownMenuItem>
                                <DropdownMenuItem asChild>
                                  <Link
                                    to={`/locations/${location.locationCode}/dashboard`}
                                  >
                                    View Dashboard
                                  </Link>
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </CardContent>
                      </Card>
                    ),
                  )}
                </div>

                {/* Table view - md and up */}
                <div className="hidden md:block divide-y divide-gray-200">
                  {locationsData.locations.map(
                    (location: Location & { _count: any }) => (
                      <div
                        key={location.id}
                        className="admin-hoverable py-3 transition-colors cursor-pointer"
                        onClick={() => handleLocationClick(location.id)}
                        title="Click to view details"
                      >
                        <div className="grid grid-cols-12 gap-4 items-start px-6">
                          <div className="col-span-4">
                            <div className="flex items-center gap-2">
                              {(location as any).isHub && (
                                <Building2 className="w-4 h-4 text-primary flex-shrink-0" />
                              )}
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                {location.name}
                              </div>
                            </div>
                            {(location as any).isHub &&
                              (location as any)._count?.childLocations > 0 && (
                                <div className="text-xs text-primary mt-0.5">
                                  Hub with{" "}
                                  {(location as any)._count.childLocations}{" "}
                                  location
                                  {(location as any)._count.childLocations !== 1
                                    ? "s"
                                    : ""}
                                </div>
                              )}
                            {(location as any).parentLocation && (
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                Child of {(location as any).parentLocation.name}
                              </div>
                            )}
                            {location.publicDescription &&
                              !(location as any).isHub &&
                              !(location as any).parentLocation && (
                                <div className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 mt-1">
                                  {location.publicDescription}
                                </div>
                              )}
                          </div>
                          <div className="col-span-2">
                            <span
                              className="text-sm font-medium"
                              style={{
                                color:
                                  (location as any).locationType?.color ||
                                  "#64748B",
                              }}
                            >
                              {(location as any).locationType?.name ||
                                "Unknown"}
                            </span>
                          </div>
                          <div className="col-span-1">
                            <span
                              className={`text-sm font-medium ${location.isPublic
                                ? "text-green-600"
                                : "text-gray-600"
                                }`}
                            >
                              {location.isPublic ? "Public" : "Private"}
                            </span>
                          </div>
                          <div className="col-span-1">
                            <div className="flex items-center gap-2">
                              <MapPin className="w-4 h-4 text-gray-400" />
                              <span className="text-sm text-gray-600 dark:text-gray-400">
                                {location.locationCode}
                              </span>
                            </div>
                          </div>
                          <div className="col-span-2">
                            <div className="text-sm text-gray-600 dark:text-gray-400">
                              {location.city}, {location.state},{" "}
                              {location.country}
                            </div>
                          </div>
                          <div className="col-span-2">
                            <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                              <div className="flex items-center gap-1">
                                <Users className="w-4 h-4" />
                                <span>{location._count?.memberships || 0}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <FileText className="w-4 h-4" />
                                <span>{location._count?.content || 0}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                <span>{location._count?.events || 0}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ),
                  )}
                </div>

                {/* No pagination - showing all locations */}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <MapPin className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No locations found</p>
                <Link to="/admin/locations/new">
                  <Button className="mt-4">Create your first location</Button>
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Floating Create button - small screens only (header hidden in card view) */}
        <Link
          to="/admin/locations/new"
          className="md:hidden fixed bottom-6 right-6 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg hover:bg-primary/90 transition-colors"
          aria-label="Create location"
        >
          <Plus className="h-6 w-6" />
        </Link>

        {/* Flyout Pane */}
        <AdminFlyoutPane
          open={!!selectedLocationId}
          onOpenChange={(open) => {
            if (!open) {
              setSelectedLocationId(null);
              setFlyoutMode("view");
            }
          }}
          title={flyoutMode === "view" ? "View Location" : flyoutMode === "delete" ? "Delete Location" : "Edit Location"}
          description={locationData?.locationCode || undefined}
          width="2xl"
          mode={flyoutMode === "delete" ? "view" : flyoutMode}
          onNavigatePrevious={handleNavigatePrevious}
          onNavigateNext={handleNavigateNext}
          hasPrevious={hasPrevious}
          hasNext={hasNext}
          headerActions={
            flyoutMode === "view" ? (
              <>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleDelete}
                  className="bg-red-600 hover:bg-red-700 text-white w-[110px]"
                >
                  Delete
                </Button>
                <Button
                  onClick={handleEdit}
                  size="sm"
                  variant="outline"
                  className="w-[110px]"
                >
                  Edit
                </Button>
                {selectedLocationId && (
                  <>
                    <Button size="sm" asChild className="w-auto ml-2">
                      <Link to={`/admin/locations/${selectedLocationId}/entries`}>
                        View Entries
                      </Link>
                    </Button>
                    <Button size="sm" asChild className="w-auto ml-2">
                      <Link to={`/admin/locations/${selectedLocationId}/edit`}>
                        Full edit
                      </Link>
                    </Button>
                  </>
                )}
              </>
            ) : flyoutMode === "delete" ? (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setFlyoutMode("view")}
                  className="w-[110px]"
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleConfirmDelete}
                  className="w-[110px]"
                >
                  Confirm Delete
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setFlyoutMode("view")}
                  className="w-[110px]"
                >
                  {hasChanges ? "Cancel" : "Close"}
                </Button>
                <Button
                  onClick={handleSave}
                  size="sm"
                  className="w-[110px]"
                  disabled={!hasChanges}
                >
                  Save
                </Button>
              </>
            )
          }
        >
          {loadingLocation && !selectedLocation ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : locationData ? (
            flyoutMode === "delete" ? (
              <FlyoutGridLayout>
                <FlyoutGridSection title="CONFIRM DELETION">
                  <div className="p-4 bg-red-50 text-red-800 rounded-md mb-4 border border-red-200">
                    Are you sure you want to delete {locationData.name}? This action cannot be undone.
                  </div>
                  {(((locationData as any)._count?.memberships > 0) ||
                    ((locationData as any)._count?.content > 0) ||
                    ((locationData as any)._count?.events > 0) ||
                    ((locationData as any)._count?.childLocations > 0)) && (
                    <div className="space-y-4">
                      <p className="text-sm font-medium">
                        This location contains associated items (memberships, events, contents, or child locations). How
                        would you like to handle them?
                      </p>

                      <div className="space-y-2">
                        <label className="flex items-center space-x-2">
                          <input
                            type="radio"
                            name="deletePolicy"
                            value="DELETE_ALL"
                            checked={deletePolicy === "DELETE_ALL"}
                            onChange={() => setDeletePolicy("DELETE_ALL")}
                            className="text-red-600 focus:ring-red-500"
                          />
                          <span>Delete everything (Cascade delete all associated entries and children)</span>
                        </label>
                        <label className="flex items-center space-x-2">
                          <input
                            type="radio"
                            name="deletePolicy"
                            value="REASSIGN"
                            checked={deletePolicy === "REASSIGN"}
                            onChange={() => setDeletePolicy("REASSIGN")}
                            className="text-blue-600 focus:ring-blue-500"
                          />
                          <span>Reassign associated items to another location</span>
                        </label>
                      </div>

                      {deletePolicy === "REASSIGN" && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <label className="block text-sm font-medium text-gray-700 mb-2">Select Target Location</label>
                          <Select value={reassignLocationId} onValueChange={setReassignLocationId}>
                            <SelectTrigger className="w-full h-10">
                              <SelectValue placeholder="Select a target location" />
                            </SelectTrigger>
                            <SelectContent>
                              {locationsData?.locations
                                .filter((l: any) => l.id !== locationData.id)
                                .map((loc: any) => (
                                  <SelectItem key={loc.id} value={loc.id}>
                                    {loc.name}
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  )}
                </FlyoutGridSection>
              </FlyoutGridLayout>
            ) : (
              <FlyoutGridLayout>
                {/* Basic Information Section */}
                <FlyoutGridSection title="BASIC INFORMATION">
                  <FlyoutGridRow
                    label="Location Name"
                    value={
                      flyoutMode === "edit" ? (
                        <Input
                          value={editFormData.name}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              name: e.target.value,
                            })
                          }
                          className="h-8 text-sm border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent px-0 -my-1"
                          placeholder="Enter location name"
                        />
                      ) : (
                        <span>{locationData.name}</span>
                      )
                    }
                  />
                  <FlyoutGridRow
                    label="Location Type"
                    value={
                      flyoutMode === "edit" ? (
                        <Select
                          value={editFormData.typeId}
                          onValueChange={(value) => setEditFormData({ ...editFormData, typeId: value })}
                        >
                          <SelectTrigger className="h-8 text-sm border-0 shadow-none focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent px-0 -my-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {locationTypesArray.map((type: any) => (
                              <SelectItem key={type.id} value={type.id}>
                                {type.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <span>{locationData.locationType?.name || "Unknown"}</span>
                      )
                    }
                  />
                  <FlyoutGridRow
                    label="Description"
                    value={
                      flyoutMode === "edit" ? (
                        <Textarea
                          value={editFormData.publicDescription}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              publicDescription: e.target.value,
                            })
                          }
                          className="text-sm border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent px-0 -my-1 min-h-[60px]"
                          placeholder="Enter description"
                        />
                      ) : (
                        <span>{locationData.publicDescription || "None"}</span>
                      )
                    }
                    isLast
                  />
                </FlyoutGridSection>

                {/* Location Settings Section */}
                <FlyoutGridSection title="LOCATION SETTINGS">
                  <FlyoutGridRow
                    label="Public Location"
                    value={
                      flyoutMode === "edit" ? (
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={editFormData.isPublic}
                            onCheckedChange={(checked) =>
                              setEditFormData({
                                ...editFormData,
                                isPublic: checked,
                              })
                            }
                          />
                          <span className="text-sm">{editFormData.isPublic ? "Public" : "Private"}</span>
                        </div>
                      ) : (
                        <span
                          className={locationData.isPublic ? "text-green-600 font-medium" : "text-gray-600"}
                        >
                          {locationData.isPublic ? "Public" : "Private"}
                        </span>
                      )
                    }
                  />
                  <FlyoutGridRow
                    label="Public Subscription"
                    value={
                      flyoutMode === "edit" ? (
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={editFormData.allowPublicSubscription}
                            onCheckedChange={(checked) =>
                              setEditFormData({
                                ...editFormData,
                                allowPublicSubscription: checked,
                              })
                            }
                          />
                          <span className="text-sm">
                            {editFormData.allowPublicSubscription ? "Allowed" : "Not Allowed"}
                          </span>
                        </div>
                      ) : (
                        <span>{locationData.allowPublicSubscription ? "Allowed" : "Not Allowed"}</span>
                      )
                    }
                  />
                  <FlyoutGridRow
                    label="Default Visibility"
                    value={
                      flyoutMode === "edit" ? (
                        <Select
                          value={editFormData.defaultVisibility}
                          onValueChange={(value) =>
                            setEditFormData({
                              ...editFormData,
                              defaultVisibility: value,
                            })
                          }
                        >
                          <SelectTrigger className="h-8 text-sm border-0 shadow-none focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent px-0 -my-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="PUBLIC">Public</SelectItem>
                            <SelectItem value="SUBSCRIBER">Subscribers Only</SelectItem>
                            <SelectItem value="VERIFIED">Verified Members Only</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <span>{locationData.defaultVisibility}</span>
                      )
                    }
                    isLast
                  />
                </FlyoutGridSection>

                {/* Hub Settings Section */}
                <FlyoutGridSection title="HUB SETTINGS">
                  <FlyoutGridRow
                    label="Is Hub"
                    value={
                      flyoutMode === "edit" ? (
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={editFormData.isHub}
                            onCheckedChange={(checked) =>
                              setEditFormData({
                                ...editFormData,
                                isHub: checked,
                              })
                            }
                          />
                          <span className="text-sm">{editFormData.isHub ? "Yes" : "No"}</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          {locationData?.isHub ? (
                            <>
                              <Building2 className="w-4 h-4 text-primary" />
                              <span className="text-primary font-medium">Hub</span>
                            </>
                          ) : (
                            <span className="text-gray-600">No</span>
                          )}
                        </div>
                      )
                    }
                  />
                  {editFormData.isHub && (
                    <FlyoutGridRow
                      label="Aggregate Content"
                      value={
                        flyoutMode === "edit" ? (
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={editFormData.aggregateChildContent}
                              onCheckedChange={(checked) =>
                                setEditFormData({
                                  ...editFormData,
                                  aggregateChildContent: checked,
                                })
                              }
                            />
                            <span className="text-sm">
                              {editFormData.aggregateChildContent ? "Show child content" : "Hub content only"}
                            </span>
                          </div>
                        ) : (
                          <span>
                            {locationData?.aggregateChildContent
                              ? "Shows content from all child locations"
                              : "Shows only hub content"}
                          </span>
                        )
                      }
                    />
                  )}
                  <FlyoutGridRow
                    label="Parent Location"
                    value={
                      flyoutMode === "edit" ? (
                        <Select
                          value={editFormData.parentLocationId || "none"}
                          onValueChange={(value) =>
                            setEditFormData({
                              ...editFormData,
                              parentLocationId: value === "none" ? null : value,
                            })
                          }
                        >
                          <SelectTrigger className="h-8 text-sm border-0 shadow-none focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent px-0 -my-1">
                            <SelectValue placeholder="None (Root Location)" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None (Root Location)</SelectItem>
                            {availableParentLocations.map((loc: any) => (
                              <SelectItem key={loc.id} value={loc.id}>
                                {loc.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <span>{locationData?.parentLocation?.name || "None (Root Location)"}</span>
                      )
                    }
                  />
                  {locationData?.childLocations && locationData.childLocations.length > 0 && (
                    <FlyoutGridRow
                      label="Child Locations"
                      value={
                        <div className="flex flex-wrap gap-1">
                          {locationData.childLocations.map((child: any) => (
                            <span
                              key={child.id}
                              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800"
                            >
                              {child.name}
                            </span>
                          ))}
                        </div>
                      }
                    />
                  )}
                  <FlyoutGridRow
                    label="Hierarchy Level"
                    value={
                      <span className="text-sm text-gray-600">
                        Level {locationData?.hierarchyLevel || 0}
                        {locationData?.hierarchyLevel === 0 ? " (Root)" : ""}
                      </span>
                    }
                    isLast
                  />
                </FlyoutGridSection>

                {/* Address Information Section */}
                <FlyoutGridSection title="ADDRESS INFORMATION">
                  <FlyoutGridRow
                    label="Street Address"
                    value={
                      flyoutMode === "edit" ? (
                        <Input
                          value={editFormData.address}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              address: e.target.value,
                            })
                          }
                          className="h-8 text-sm border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent px-0 -my-1"
                          placeholder="Enter street address"
                        />
                      ) : (
                        <span>{locationData.address || "Not set"}</span>
                      )
                    }
                  />
                  <FlyoutGridRow
                    label="City"
                    value={
                      flyoutMode === "edit" ? (
                        <Input
                          value={editFormData.city}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              city: e.target.value,
                            })
                          }
                          className="h-8 text-sm border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent px-0 -my-1"
                          placeholder="Enter city"
                        />
                      ) : (
                        <span>{locationData.city}</span>
                      )
                    }
                  />
                  <FlyoutGridRow
                    label="State/Province"
                    value={
                      flyoutMode === "edit" ? (
                        <Input
                          value={editFormData.state}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              state: e.target.value,
                            })
                          }
                          className="h-8 text-sm border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent px-0 -my-1"
                          placeholder="Enter state/province"
                        />
                      ) : (
                        <span>{locationData.state}</span>
                      )
                    }
                  />
                  <FlyoutGridRow
                    label="Country"
                    value={
                      flyoutMode === "edit" ? (
                        <Input
                          value={editFormData.country}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              country: e.target.value,
                            })
                          }
                          className="h-8 text-sm border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent px-0 -my-1"
                          placeholder="Enter country"
                        />
                      ) : (
                        <span>{locationData.country}</span>
                      )
                    }
                  />
                  <FlyoutGridRow
                    label="Postal Code"
                    value={
                      flyoutMode === "edit" ? (
                        <Input
                          value={editFormData.postalCode}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              postalCode: e.target.value,
                            })
                          }
                          className="h-8 text-sm border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent px-0 -my-1"
                          placeholder="Enter postal code"
                        />
                      ) : (
                        <span>{locationData.postalCode || "Not set"}</span>
                      )
                    }
                  />
                  <FlyoutGridRow
                    label="Timezone"
                    value={
                      flyoutMode === "edit" ? (
                        <Select
                          value={editFormData.timezone}
                          onValueChange={(value) =>
                            setEditFormData({
                              ...editFormData,
                              timezone: value,
                            })
                          }
                        >
                          <SelectTrigger className="h-8 text-sm border-0 shadow-none focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent px-0 -my-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="UTC">UTC</SelectItem>
                            <SelectItem value="America/New_York">Eastern Time</SelectItem>
                            <SelectItem value="America/Chicago">Central Time</SelectItem>
                            <SelectItem value="America/Denver">Mountain Time</SelectItem>
                            <SelectItem value="America/Los_Angeles">Pacific Time</SelectItem>
                            <SelectItem value="Europe/London">London</SelectItem>
                            <SelectItem value="Europe/Paris">Paris</SelectItem>
                            <SelectItem value="Asia/Tokyo">Tokyo</SelectItem>
                            <SelectItem value="Asia/Shanghai">Shanghai</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <span>{locationData.timezone}</span>
                      )
                    }
                    isLast
                  />
                </FlyoutGridSection>

                {/* Coordinates Section */}
                <FlyoutGridSection title="COORDINATES">
                  <FlyoutGridRow
                    label="Latitude"
                    value={
                      flyoutMode === "edit" ? (
                        <Input
                          type="number"
                          step="any"
                          value={editFormData.latitude}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              latitude: e.target.value,
                            })
                          }
                          className="h-8 text-sm border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent px-0 -my-1"
                          placeholder="Enter latitude"
                        />
                      ) : (
                        <span>{locationData.latitude || "Not set"}</span>
                      )
                    }
                  />
                  <FlyoutGridRow
                    label="Longitude"
                    value={
                      flyoutMode === "edit" ? (
                        <Input
                          type="number"
                          step="any"
                          value={editFormData.longitude}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              longitude: e.target.value,
                            })
                          }
                          className="h-8 text-sm border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent px-0 -my-1"
                          placeholder="Enter longitude"
                        />
                      ) : (
                        <span>{locationData.longitude || "Not set"}</span>
                      )
                    }
                    isLast
                  />
                </FlyoutGridSection>

                {/* Statistics Section (View Only) */}
                {flyoutMode === "view" && (
                  <FlyoutGridSection title="STATISTICS">
                    <FlyoutGridRow
                      label="Members"
                      value={
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-gray-500" />
                          <span>{(locationData as any)._count?.memberships || 0}</span>
                        </div>
                      }
                    />
                    <FlyoutGridRow
                      label="Content Items"
                      value={
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-gray-500" />
                          <span>{(locationData as any)._count?.content || 0}</span>
                        </div>
                      }
                    />
                    <FlyoutGridRow
                      label="Events"
                      value={
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-gray-500" />
                          <span>{(locationData as any)._count?.events || 0}</span>
                        </div>
                      }
                      isLast
                    />
                  </FlyoutGridSection>
                )}
              </FlyoutGridLayout>
            )
          ) : null}
        </AdminFlyoutPane>
      </div>
    </DefaultLayout>
  );
};

export default AdminLocationsPage;
